import {SchemaCollaborator} from 'auth/PermissionConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NameTemplate, NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getSpecError from 'ui-library/spec-editor/getSpecError';
import * as net from 'util/Net';

import {CollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';

export function getStudySchemaListingURL(): string {
  return '/study-schemas/listing';
}

export function getCreateStudySchemaURL(): string {
  return '/study-schemas/create';
}

export function getStudySchemaURL(schemaId: string): string {
  // TODO implement start route for this URL
  return `/study-schemas/${schemaId}`;
}

export interface StudySchemaSpec {
  fields: Array<SchemaField>;
  idPrefix: string;
  isDesignApprovalRequired: boolean;
  template_api_ids: Array<string>;
  studyType: StudyType;
}

interface StudySchemaCreateParams {
  ownerId: string;
  name: string;
  collaborators: Array<SchemaCollaborator>;
  spec: StudySchemaSpec;
}

interface CreateStudySchemaResponse {
  schemaId: string;
}

export interface UpdateSchemaCollaboratorsRequest {
  collaborators: Array<SchemaCollaborator>;
}

interface UpdateSchemaCollaboratorsResponse {
  schema: CollaboratorInfoGraphQL;
}

export function updateCollaborators(
  schemaId: string,
  data: UpdateSchemaCollaboratorsRequest
): Promise<UpdateSchemaCollaboratorsResponse> {
  return net.patchJSONPromise({url: `/1/api/study-schemas/${schemaId}/collaborators`, data});
}

export function createStudySchema(params: StudySchemaCreateParams): Promise<CreateStudySchemaResponse> {
  return net.postJSONPromise({
    url: '/1/api/study-schemas',
    data: params,
  });
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/study-schemas:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/study-schemas:bulk-unarchive',
    data: {ids},
  });
}

interface StudySchemaUpdateParams {
  schemaId: string;
  name?: string;
  sqlIdentifier?: string;
  spec: StudySchemaSpec;
}
export function updateStudySchema({
  schemaId,
  name,
  sqlIdentifier,
  spec,
}: StudySchemaUpdateParams): Promise<CreateStudySchemaResponse> {
  return net.patchJSONPromise({
    url: `/1/api/study-schemas/${schemaId}`,
    data: {name, sqlIdentifier, spec},
  });
}

export async function withSpecErrorHandling<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch (response) {
    const specError = getSpecError(response);
    if (specError) {
      specError.errors.forEach((error) => {
        notifications.error(error);
      });
      return null;
    }
    throw response;
  }
}

export interface SetNameTemplateResponse {
  studySchemaId: string;
  nameTemplate: NameTemplate;
}

export interface SetNameTemplateProps {
  schemaId: string;
  nameTemplateParts: Array<NameTemplatePart>;
}

export function setStudyNameTemplateParts({
  schemaId,
  nameTemplateParts,
}: SetNameTemplateProps): Promise<SetNameTemplateResponse> {
  return net.postJSONPromise({
    url: `/1/api/study-schemas/${schemaId}/actions/set-study-name-template`,
    data: {nameTemplateParts},
  });
}

export function setProcedureRunNameTemplateParts({
  schemaId,
  nameTemplateParts,
}: SetNameTemplateProps): Promise<SetNameTemplateResponse> {
  return net.postJSONPromise({
    url: `/1/api/study-schemas/${schemaId}/actions/set-procedure-run-name-template`,
    data: {nameTemplateParts},
  });
}
